"use client";

import React, { useRef, useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Sparkles } from "lucide-react";
import Sketch from "react-p5";
import p5Types from "p5";
const StarfieldBackground = () => {
  const stars = useRef<{
    x: number;
    y: number;
    z: number;
    pz: number;
  }[]>([]);
  const speed = 2;
  const setup = (p5: p5Types, canvasParentRef: Element) => {
    p5.createCanvas(p5.windowWidth, p5.windowHeight).parent(canvasParentRef);
    stars.current = new Array(400).fill(null).map(() => ({
      x: p5.random(-p5.width, p5.width),
      y: p5.random(-p5.height, p5.height),
      z: p5.random(p5.width),
      pz: p5.random(p5.width)
    }));
  };
  const draw = (p5: p5Types) => {
    p5.background(10, 10, 15);
    p5.translate(p5.width / 2, p5.height / 2);
    stars.current.forEach(star => {
      star.z = star.z - speed;
      if (star.z < 1) {
        star.z = p5.width;
        star.x = p5.random(-p5.width, p5.width);
        star.y = p5.random(-p5.height, p5.height);
        star.pz = star.z;
      }
      const sx = p5.map(star.x / star.z, 0, 1, 0, p5.width);
      const sy = p5.map(star.y / star.z, 0, 1, 0, p5.height);
      const r = p5.map(star.z, 0, p5.width, 4, 0);
      const alpha = p5.map(star.z, 0, p5.width, 255, 0);
      p5.fill(188, 240, 255, alpha);
      p5.noStroke();
      p5.ellipse(sx, sy, r, r);
      const px = p5.map(star.x / star.pz, 0, 1, 0, p5.width);
      const py = p5.map(star.y / star.pz, 0, 1, 0, p5.height);
      star.pz = star.z;
      p5.stroke(188, 240, 255, alpha * 0.5);
      p5.line(px, py, sx, sy);
    });
  };
  const windowResized = (p5: p5Types) => {
    p5.resizeCanvas(p5.windowWidth, p5.windowHeight);
  };
  return <div data-section-id="29829" className="absolute inset-0 z-0 opacity-60 pointer-events-none">
      <Sketch setup={setup} draw={draw} windowResized={windowResized} />
    </div>;
};
export default function StartYourProject() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <section id="instant-estimate-system" className="dark bg-background text-foreground relative w-full py-24 overflow-hidden flex items-center justify-center min-h-[600px]">
      {}
      {mounted && <StarfieldBackground />}

      {}
      <div className="container relative z-10 px-4 md:px-6">
        <div className="mx-auto max-w-3xl">
          {}
          <div className="relative rounded-3xl p-[3px] bg-gradient-to-br from-primary via-secondary to-accent shadow-[0_0_50px_-10px_rgba(255,87,34,0.3)]">
            
            {}
            <Card className="relative h-full w-full overflow-hidden rounded-[21px] border-0 bg-card/90 backdrop-blur-xl">
              <CardContent className="flex flex-col items-center text-center p-10 md:p-16 gap-8">
                
                {}
                <div className="rounded-full bg-primary/10 p-4 ring-1 ring-primary/20 shadow-[0_0_20px_rgba(255,87,34,0.3)]">
                  <Sparkles className="h-8 w-8 text-primary animate-pulse" />
                </div>

                {}
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-foreground">Material Estimate Engine</h2>
                  <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">Know what you need in advanced. Our industrial-grade calculator provides instant yardage.</p>
                </div>

                {}
                <div className="flex flex-col sm:flex-row gap-4 w-full justify-center pt-4">
                  <Button asChild size="lg" className="h-14 px-8 text-lg font-black uppercase tracking-tight rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_-5px_var(--color-primary)] hover:shadow-[0_0_50px_-5px_var(--color-primary)] transition-all duration-300 hover:scale-105">
                    <Link to="/MaterialShop">Browse Materials<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-2 h-5 w-5" aria-hidden="true" data-ast-id="f:/src/pages/Home/sections/StartYourProject.tsx|sig:bwau5e" data-ast-start="4197" data-ast-end="4236"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
                  </Button>
                  
                  <Button asChild variant="outline" size="lg" className="h-14 px-8 text-lg font-bold uppercase tracking-tight rounded-sm border-primary/20 hover:bg-primary/10 hover:text-primary hover:border-primary/50 transition-all duration-300">
                    
                  </Button>
                </div>

                {}
                <div className="pt-8 flex flex-wrap justify-center gap-x-8 gap-y-4 text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                  <span className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    40 Years of Heavy Iron
                  </span>
                  <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-secondary" data-ast-id="f:/src/pages/Home/sections/StartYourProject.tsx|sig:nd8m65" data-ast-start="5594" data-ast-end="5652"></span>Precision Site Development</span>
                  <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-accent-foreground" data-ast-id="f:/src/pages/Home/sections/StartYourProject.tsx|sig:o2qu4x" data-ast-start="5626" data-ast-end="5692"></span>Sand | Gravel | Aggregate Production and Delivery</span>
                </div>

              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}